/**
 * AIHelperContext — manages the AI helper state across the game view:
 * selection, panel open/close, response, deep-link loading.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';
import { requestAIHelp, fetchAIHelperEntry } from '../api/aiHelper';
import { useGettoncini } from './GettonciniContext';
import { useAppResources } from './AppResourcesContext';
import { useAlertQueueActions } from './AlertQueueContext';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import { trackAiHelperPanelOpened } from '../analytics';

const AIHelperContext = createContext(null);
const AI_HELPER_POLL_INTERVAL_MS = 2000;
const AI_HELPER_POLL_TIMEOUT_MS = 30000;
const AI_HELPER_RETRY_LATER_MESSAGE = 'Si è verificato un errore temporaneo. Riprova tra poco.';

export const useAIHelper = () => {
  const ctx = useContext(AIHelperContext);
  if (!ctx) {
    // Safe defaults outside provider (admin, preview)
    return {
      selectedText: '',
      selection: null,
      puzzleInstanceId: null,
      gameSlug: null,
      isOpen: false,
      isConfirming: false,
      isLoading: false,
      response: '',
      error: null,
      setSelection: () => {},
      requestHelp: async () => {},
      dismiss: () => {},
      loadEntry: async () => {},
    };
  }
  return ctx;
};

export const AIHelperProvider = ({ children }) => {
  const { isSignedIn } = useUser();
  const { getToken } = useAuth();
  const { updateBalance } = useGettoncini();
  const { updateAppResourcesLocally } = useAppResources();
  const { queueAlert } = useAlertQueueActions();

  // Selection state (set by AIHelperTrigger via setSelection)
  const [selectedText, setSelectedText] = useState('');
  const [selection, setSelectionObj] = useState(null);
  const [puzzleInstanceId, setPuzzleInstanceId] = useState(null);
  const [gameSlug, setGameSlug] = useState(null);

  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState('');
  const [error, setError] = useState(null);

  // Guard against stale request results
  const requestIdRef = useRef(0);

  // In-memory cache of successful AI helper responses for this session.
  // Key: `${puzzleInstanceId}_${canonicalSelection}`, Value: response text.
  // Prevents redundant API calls (and charges) when the user re-selects
  // the same word/item within the same page session.
  const responseCacheRef = useRef(new Map());

  // Derived: panel is open in confirmation mode (before request is sent)
  const isConfirming = isOpen && !isLoading && !response && !error;

  /**
   * Build a deterministic cache key for a given puzzleInstanceId + selection.
   */
  const _cacheKey = (pid, sel) => {
    const sortedSel = JSON.stringify(sel, Object.keys(sel).sort());
    return `${pid}_${sortedSel}`;
  };

  /**
   * Called by AIHelperTrigger when a valid component selection is made.
   * If a cached response exists from a previous request in this session,
   * the panel opens directly with the response (skipping confirmation).
   * Otherwise, opens the panel in confirmation mode.
   */
  const setSelection = useCallback(({ text, selection: sel, puzzleInstanceId: pid, gameSlug: slug, staticResponse }) => {
    setSelectedText(text);
    setSelectionObj(sel);
    if (pid) setPuzzleInstanceId(pid);
    setGameSlug(slug || null);

    // Check session cache for a previously-fetched response
    const cached = pid && sel ? responseCacheRef.current.get(_cacheKey(pid, sel)) : null;

    setResponse(staticResponse || cached || '');
    setError(null);
    setIsLoading(false);
    setIsOpen(true);
    trackAiHelperPanelOpened({
      source_surface: 'ai_helper_trigger',
      game_slug: slug || null,
      selection_length: (text || '').length,
      selection_component: sel?.component || null,
      selection_param_count: sel ? Math.max(Object.keys(sel).length - 1, 0) : 0,
      is_signed_in: !!isSignedIn,
    });
  }, [isSignedIn]);

  /**
   * Initiate an AI helper request — awaits the full response from the API.
   */
  const requestHelp = useCallback(
    async (puzzleInstanceId, sel, text) => {
      if (!isSignedIn) return;

      const currentRequestId = ++requestIdRef.current;

      setIsOpen(true);
      setIsLoading(true);
      setResponse('');
      setError(null);

      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(
          () => null
        );

        const data = await requestAIHelp(puzzleInstanceId, sel, text, token);

        if (currentRequestId !== requestIdRef.current) return;

        if (data?.app_resources) {
          updateAppResourcesLocally(data.app_resources);
          queueResourceAlertsFromPayload({
            queueAlert,
            payload: data,
            isAnonymous: false,
          });
        }

        // Update balance from server
        if (data.balance !== undefined) {
          updateBalance(data.balance);
        }

        if (data?.status === 'pending' && data?.entry_id) {
          const pollIntervalMs = Number.isFinite(Number(data.poll_after_ms))
            ? Number(data.poll_after_ms)
            : AI_HELPER_POLL_INTERVAL_MS;
          const pollStartedAt = Date.now();

          while (
            currentRequestId === requestIdRef.current
            && Date.now() - pollStartedAt < AI_HELPER_POLL_TIMEOUT_MS
          ) {
            await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
            if (currentRequestId !== requestIdRef.current) return;

            let entry;
            try {
              entry = await fetchAIHelperEntry(data.entry_id, token);
            } catch (_pollError) {
              continue;
            }
            if (currentRequestId !== requestIdRef.current) return;
            if (!entry || entry.status === 'pending') {
              continue;
            }

            if (entry.status === 'succeeded') {
              const resolvedText = entry.response_text || '';
              setResponse(resolvedText);
              setIsLoading(false);
              if (puzzleInstanceId && sel) {
                responseCacheRef.current.set(
                  _cacheKey(puzzleInstanceId, sel),
                  resolvedText,
                );
              }
              return;
            }

            setIsLoading(false);
            setError({
              error: entry.error || 'LLM_ERROR',
              message: (
                entry.error === 'LLM_ERROR'
                  ? AI_HELPER_RETRY_LATER_MESSAGE
                  : (entry.message || 'Si è verificato un errore.')
              ),
            });
            return;
          }

          if (currentRequestId !== requestIdRef.current) return;
          setIsLoading(false);
          setError({
            error: 'AI_HELPER_TIMEOUT',
            message: 'La risposta sta impiegando più del previsto. Riprova tra poco.',
          });
          return;
        }

        const resolvedText = data?.response_text || '';
        setResponse(resolvedText);
        setIsLoading(false);

        // Cache the response so repeat selections are instant
        if (puzzleInstanceId && sel && resolvedText) {
          responseCacheRef.current.set(
            _cacheKey(puzzleInstanceId, sel),
            resolvedText,
          );
        }
      } catch (err) {
        if (currentRequestId !== requestIdRef.current) return;
        setIsLoading(false);

        // err is an error object from the API: { error, message, balance? }
        if (err && err.error) {
          if (err.error === 'LLM_ERROR') {
            err.message = AI_HELPER_RETRY_LATER_MESSAGE;
          }
          setError(err);
          if (err?.app_resources) {
            updateAppResourcesLocally(err.app_resources);
            queueResourceAlertsFromPayload({
              queueAlert,
              payload: err,
              isAnonymous: false,
            });
          }
          if (err.balance !== undefined) {
            updateBalance(err.balance);
          }
        } else {
          setError({ error: 'UNEXPECTED', message: 'Si è verificato un errore.' });
        }
      }
    },
    [isSignedIn, getToken, updateBalance, updateAppResourcesLocally, queueAlert]
  );

  /**
   * Load a previously saved AI helper entry (for deep-link from history).
   */
  const loadEntry = useCallback(
    async (entryId) => {
      if (!isSignedIn) return;

      setIsOpen(true);
      setIsLoading(true);
      setError(null);
      setResponse('');

      try {
        const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(
          () => null
        );
        const entry = await fetchAIHelperEntry(entryId, token);
        setSelectedText(entry.selected_text);
        setSelectionObj(entry.selection);
        setResponse(entry.response_text || '');
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        setError({ error: 'LOAD_ERROR', message: 'Impossibile caricare la risposta.' });
      }
    },
    [isSignedIn, getToken]
  );

  /**
   * Close the panel and reset state.
   * Also cleans up the ?ai-entry query param if present.
   */
  const dismiss = useCallback(() => {
    requestIdRef.current++; // cancel any in-flight request
    setIsOpen(false);
    setIsLoading(false);
    setResponse('');
    setError(null);
    setSelectedText('');
    setSelectionObj(null);
    setPuzzleInstanceId(null);
    setGameSlug(null);
  }, []);

  // ── Navigation & deep-link handling ──────────────────────────────────
  // Single unified effect so deep-link detection and pathname-change
  // dismiss don't race against each other in the same render.
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandledRef = useRef(null);
  const prevPathnameRef = useRef(location.pathname);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const aiEntryId = params.get('ai-entry');
    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    prevPathnameRef.current = location.pathname;

    if (aiEntryId && aiEntryId !== deepLinkHandledRef.current) {
      // Deep-link takes priority — but only mark as handled once auth is
      // ready, so the effect retries when isSignedIn flips to true.
      if (isSignedIn) {
        deepLinkHandledRef.current = aiEntryId;
        loadEntry(aiEntryId);
      }
    } else if (pathnameChanged) {
      // Normal navigation to a different page: dismiss
      deepLinkHandledRef.current = null;
      dismiss();
    }
  }, [location.pathname, location.search, dismiss, loadEntry, isSignedIn]);

  // Clean up ?ai-entry from the URL when the panel transitions open → closed
  // (skip initial mount where isOpen starts as false).
  const prevIsOpenRef = useRef(isOpen);
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    prevIsOpenRef.current = isOpen;

    if (wasOpen && !isOpen) {
      deepLinkHandledRef.current = null;
      const params = new URLSearchParams(location.search);
      if (params.has('ai-entry')) {
        params.delete('ai-entry');
        const cleanSearch = params.toString();
        navigate(
          { pathname: location.pathname, search: cleanSearch ? `?${cleanSearch}` : '' },
          { replace: true }
        );
      }
    }
  }, [isOpen, location.pathname, location.search, navigate]);

  const value = useMemo(
    () => ({
      selectedText,
      selection,
      puzzleInstanceId,
      gameSlug,
      isOpen,
      isConfirming,
      isLoading,
      response,
      error,
      setSelection,
      requestHelp,
      dismiss,
      loadEntry,
    }),
    [
      selectedText,
      selection,
      puzzleInstanceId,
      gameSlug,
      isOpen,
      isConfirming,
      isLoading,
      response,
      error,
      setSelection,
      requestHelp,
      dismiss,
      loadEntry,
    ]
  );

  return (
    <AIHelperContext.Provider value={value}>{children}</AIHelperContext.Provider>
  );
};
